import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "./contexts/LanguageContext";
import ScrollToTop from "./components/ScrollToTop";
import RedirectsHandler from "./components/RedirectsHandler";
import HoursNoticePopup from "./components/HoursNoticePopup";
import HappyHourPopup from "./components/HappyHourPopup";
import { useShimmerVariation } from "./hooks/useShimmerVariation";
import { lazyWithRetry } from "./lib/lazyWithRetry";

const Index = lazyWithRetry(() => import("./pages/Index"));
const MenuPage = lazyWithRetry(() => import("./pages/MenuPage"));
const GalleryPage = lazyWithRetry(() => import("./pages/GalleryPage"));
const EventsPage = lazyWithRetry(() => import("./pages/EventsPage"));
const GiftCardPage = lazyWithRetry(() => import("./pages/GiftCardPage"));
const AccessibilityPage = lazyWithRetry(() => import("./pages/AccessibilityPage"));
const PrivacyPage = lazyWithRetry(() => import("./pages/PrivacyPage"));
const TermsPage = lazyWithRetry(() => import("./pages/TermsPage"));
const ParkingPage = lazyWithRetry(() => import("./pages/ParkingPage"));
const ReviewRedirect = lazyWithRetry(() => import("./pages/ReviewRedirect"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const Popups = () => {
  const { pathname } = useLocation();
  if (pathname === "/review") return null;
  return (<><HoursNoticePopup /><HappyHourPopup /></>);
};

const queryClient = new QueryClient();

const App = () => {
  useShimmerVariation();

  return (
    <HelmetProvider>
      <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <RedirectsHandler />
              <Popups />
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/menu" element={<MenuPage />} />
                  <Route path="/gallery" element={<GalleryPage />} />
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/gift-card" element={<GiftCardPage />} />
                  <Route path="/accessibility" element={<AccessibilityPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/parking" element={<ParkingPage />} />
                  <Route path="/review" element={<ReviewRedirect />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
};

export default App;
